<template>
  <div class="d-flex justify-content-end">
    <McTableFilter
      :activeFilters="localFilters"
      :parseFilterDataMappings="parseFilterDataMappings"
      :presetFullObjects="vehicleObject"
      :hasDefaultFilter="checkFilterIsActive"
      @submit="handleSaveFilter"
      @update:showModal="onShowModal($event)"
      @reset="handleReset"
      @getFullObjects="updateLocalFullObjects"
    >
      <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
        <div class="mc-modal__filter-item">
          <FormulateInput
            v-model="localFilters.date"
            name="date"
            isShow="true"
            :label="$t('media.modalFilter.date.label')"
            :placeholder="$t('media.modalFilter.date.placeholder')"
            type="date-picker"
            datepickerType="date"
            valueType="YYYY-MM-DD"
            format="DD MMM YYYY"
            titleFormat="DD MMM YYYY"
            :confirmText="$t('global.action.apply')"
            :clearable="false"
            :disabled-date="disabledRange"
            @close="closePicker"
          />
        </div>
        <div class="mc-modal__filter-item">
          <FormulateInput
            v-bind="vehicleFields"
            :fullObject="fullObjects['vehicleId']"
            :value="localFilters.vehicleId"
            @update:formValues="updateFormValues({ key: 'vehicleId', value: $event })"
            @update:fullObject="updateFullObjects({ key: 'vehicleId', value: $event })"
          />
        </div>
        <div class="mc-modal__filter-item">
          <FormulateInput
            type="group"
            name="mediaType"
            :label="$t('media.modalFilter.mediaType.label')"
            :value="(formValues.mediaType = localFilters.mediaType)"
          >
            <div class="mc-modal__filter-button-group">
              <button
                type="button"
                class="mc-modal__filter-button"
                :class="{ 'mc-modal__filter-button--active': localFilters.mediaType.all }"
                @click="handleSelectMediaType('all')"
              >
                {{ $t('master.status.all') }}
              </button>
              <button
                type="button"
                class="mc-modal__filter-button"
                :class="{ 'mc-modal__filter-button--active': localFilters.mediaType.photo }"
                @click="handleSelectMediaType('photo')"
              >
                {{ $t('global.photo') }}
              </button>
              <button
                type="button"
                class="mc-modal__filter-button"
                :class="{ 'mc-modal__filter-button--active': localFilters.mediaType.video }"
                @click="handleSelectMediaType('video')"
              >
                {{ $t('global.action.video') }}
              </button>
            </div>
          </FormulateInput>
        </div>
      </template>
    </McTableFilter>
  </div>
</template>
<script>
import McTableFilter from '@/components/McTableFilter'
import { Helper } from '@/mixin/helper'
import datePickerLang from '@/helpers/date-picker-lang'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { GET_VEHICLE_VIDEO_FILTER } from '../../../../services/api/trackvideo/video'

moment.locale('id')

export default {
  components: {
    McTableFilter,
  },
  mixins: [Helper],
  apollo: {
    vehicles: {
      query: GET_VEHICLE_VIDEO_FILTER,
      variables() {
        return {
          filter: {
            motionStatus: this.hasAccess(['assisted_motion_status:read'])
              ? ['assisted', 'idle', 'moving', 'offline', 'parking', 'unknown']
              : undefined,
            orderBy: JSON.stringify({ licensePlate: 'asc' }),
          },
        }
      },
      result({ data, loading }) {
        if (loading) return

        const vgroupMap = new Map()
        const vehicles = data.vehicleVideoTrackers.vehicleVideoTrackers.map((v) => {
          v.vehicle.vehicle2Vgroup.forEach((vg) => {
            if (!vgroupMap.has(vg.vgroupId)) {
              vgroupMap.set(vg.vgroupId, { id: vg.vgroupId, name: vg.vgroup.name })
            }
          })
          return {
            ...v,
            calculatedLicensePlate: v.parent?.vehicle?.licensePlate ?? v.vehicle.licensePlate,
          }
        })
        this.vgroups = Array.from(vgroupMap.values())
        this.vehicleList = vehicles
      },
    },
  },
  props: {
    activeFilters: {
      required: true,
      type: Object,
    },
    vehicleObject: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      vgroups: [],
      vehicleList: [],
      localFilters: {
        date: moment().format('YYYY-MM-DD'),
        vehicleId: [],
        mediaType: {
          all: false,
          photo: true,
          video: false,
        },
        pastDate: moment().subtract(3, 'months'),
        futureEnd: moment().add(3, 'months'),
        currentPastDate: moment().subtract(3, 'months'),
        currentFutureEnd: moment().add(3, 'months'),
      },
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),

    datePickerLang() {
      return datePickerLang()
    },

    vehicleFields() {
      const tableFields = [
        {
          label: this.$t('report.fields.licensePlate'),
          key: 'calculatedLicensePlate',
          isSortable: true,
        },
        {
          label: this.$t('report.fields.vehicleGroup'),
          key: 'vehicle.vehicle2Vgroup',
          isSortable: false,
          formatter: (value) => {
            return value && value.length > 0 ? value.map((v) => v.vgroup.name).join(', ') : '-'
          },
        },
      ]
      return {
        name: 'vehicleId',
        isShow: true,
        label: this.$t('report.fields.licensePlate'),
        type: 'trackvision-filter-value-help',
        placeholder: `${this.$t('global.action.choose')} ${this.$t('report.fields.licensePlate')}`,
        trackBy: 'vehicleId',
        displayedBy: 'calculatedLicensePlate',
        listGetter: (filter) => this.getVehicleList(filter),
        multiple: true,
        tableFields: tableFields,
        filters: [
          {
            label: this.$t('report.fields.vehicleGroup'),
            key: 'vgroup',
            data: this.vgroups,
            model: [],
          },
        ],
      }
    },

    parseFilterDataMappings() {
      return {
        date: 'date',
        vehicleId: 'vehicleId',
        mediaType: 'mediaType',
      }
    },
    checkFilterIsActive() {
      const isFilterVehicleActive = this.activeFilters.vehicleId?.length > 0
      const isFilterMediaTypeActive = !this.activeFilters.mediaType.photo
      const isFilterDateActive = this.activeFilters.date !== moment().format('YYYY-MM-DD')
      return isFilterDateActive || isFilterMediaTypeActive || isFilterVehicleActive
    },
  },
  methods: {
    onShowModal(show) {
      if (show) {
        this.localFilters = {
          ...this.activeFilters,
          vehicleId: [...(this.activeFilters.vehicleId ?? [])],
          mediaType: { ...(this.activeFilters.mediaType ?? {}) },
        }

        this.pastDate = moment().subtract(3, 'months')
        this.futureEnd = moment().add(3, 'months')
        this.currentPastDate = moment().subtract(3, 'months')
        this.currentFutureEnd = moment().add(3, 'months')

        this.$forceUpdate()
      }
    },
    handleSaveFilter(values) {
      let objValues = {}
      values.forEach((v) => {
        if (!v.values && v.key === 'date') {
          v.values = moment().format('YYYY-MM-DD')
        } else if (!v.values && v.key === 'vehicleId') {
          v.values = []
        } else if (!v.values && v.key === 'mediaType') {
          v.values = {
            all: false,
            photo: true,
            video: false,
          }
        }
        objValues[v.key] = v.values
      })
      this.localFilters = objValues
      this.$emit('applyFilters', objValues)
    },

    async getVehicleList(opt) {
      const { orderBy, ...otherOpt } = opt

      let orderByLicensePlate = 'asc'
      if (orderBy) {
        orderByLicensePlate = JSON.parse(orderBy)[0].licensePlate
      }

      let calculatedVehicleList = [...this.vehicleList]
      calculatedVehicleList.sort((a, b) => {
        if (orderByLicensePlate === 'asc') {
          return a.calculatedLicensePlate.localeCompare(b.calculatedLicensePlate)
        } else {
          return b.calculatedLicensePlate.localeCompare(a.calculatedLicensePlate)
        }
      })
      if (otherOpt?.search) {
        calculatedVehicleList = calculatedVehicleList.filter((v) =>
          v.calculatedLicensePlate.toLowerCase().includes(otherOpt.search.toLowerCase())
        )
      }
      if (otherOpt?.vgroup) {
        calculatedVehicleList = calculatedVehicleList.filter((v) =>
          v.vehicle.vehicle2Vgroup.some((vg) => otherOpt.vgroup.includes(vg.vgroupId))
        )
      }
      return {
        data: calculatedVehicleList,
        ids: calculatedVehicleList.map((v) => v.vehicleId),
        pagination: {
          page: 1,
          show: calculatedVehicleList.length,
          total: calculatedVehicleList.length,
        },
      }
    },
    handleSelectMediaType(type) {
      Object.keys(this.localFilters.mediaType).forEach((key) => {
        if (key === type) {
          this.localFilters.mediaType[key] = true
        } else {
          this.localFilters.mediaType[key] = false
        }
      })
    },
    disabledRange: function (date) {
      let limit = new Date()
      return (
        date <= this.pastDate ||
        date >= this.futureEnd ||
        date <= this.currentPastDate ||
        date >= this.currentFutureEnd ||
        date > limit
      )
    },
    closePicker() {
      this.currentPastDate = moment().subtract(3, 'months')
      this.currentFutureEnd = moment().add(3, 'months')
    },
    handleReset() {
      this.$emit('applyFullObjects', {})
    },
    updateLocalFullObjects(value) {
      this.$emit('applyFullObjects', value)
    },
  },
}
</script>
